import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject, switchMap } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { BaseService } from '../../../../../services/base.service';
import { AvatarForm, PasswordForm, ProfileForm } from './profile.interface';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ProfileService } from '../../../../../services/profile.service';
import { ActivatedRoute, Router } from '@angular/router';
import { IError } from '../../../../../interfaces';
import { ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { Tab } from '../../../../../components/tabs/interfaces/tab.interface';
import { ProfileTab } from '../../../../../components/tabs/enums/tabs.enum';
import { parsePhoneWithCode } from '../../../../../helpers';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();
  private readonly tabQuery: string = this.activatedRoute.snapshot.queryParams['tab'];
  protected readonly tabs: Tab<ProfileTab>[] = [
    {
      name: 'profile',
      value: ProfileTab.PROFILE,
      default: true,
    },
    {
      name: 'settings',
      value: ProfileTab.SETTINGS,
      default: false,
    },
  ];
  protected activeTab!: Tab<ProfileTab>;
  protected profileForm!: FormGroup<ProfileForm>;
  protected passwordForm!: FormGroup<PasswordForm>;
  protected avatarForm!: FormGroup<AvatarForm>;
  protected formSubmitted: boolean = false;

  constructor(
    private readonly notificationService: NotificationService,
    private readonly profileService: ProfileService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit() {
    if (this.tabQuery === 'settings') {
      this.tabs[0].default = false;
      this.tabs[1].default = true;
      this.onChangeTab(this.tabs[1]);
      const snapshot = this.activatedRoute.snapshot;
      const params: any = { ...snapshot.queryParams };
      delete params.tab;
      this.router.navigate([], { queryParams: params });
    } else {
      this.tabs[0].default = true;
      this.tabs[1].default = false;
      this.onChangeTab(this.tabs[0]);
    }

    const phone = parsePhoneWithCode(this.baseService.user.phone);

    this.profileForm = new FormGroup<any>({
      first_name: new FormControl(this.baseService.user.first_name, Validators.required),
      last_name: new FormControl(this.baseService.user.last_name, Validators.required),
      phone: new FormControl(phone, [Validators.pattern(/^380/), Validators.minLength(12), Validators.required]),
      email: new FormControl(this.baseService.user.email, Validators.required),
    });

    this.passwordForm = new FormGroup<any>(
      {
        new_password: new FormControl('', Validators.required),
        confirm_password: new FormControl('', Validators.required),
      },
      { validators: [() => this.passwordsMatchValidator()] },
    );

    this.avatarForm = new FormGroup<AvatarForm>({
      file: new FormControl(null, [Validators.required]),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onChangeTab(selectedTab: Tab<ProfileTab>) {
    this.activeTab = selectedTab;
  }

  saveProfile() {
    this.formSubmitted = true;
    if (this.profileForm.valid) {
      const data = {
        first_name: this.profileForm.controls.first_name.value,
        last_name: this.profileForm.controls.last_name.value,
      };
      const phone = this.profileForm.controls.phone.value;
      if (
        data.first_name !== this.baseService.currentUserInfo.first_name ||
        data.last_name !== this.baseService.currentUserInfo.last_name
      ) {
        this.profileService
          .update(data)
          .pipe(switchMap(() => this.baseService.getProfile()))
          .subscribe({
            next: () => {
              this.formSubmitted = false;
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_updated,
              );
            },
          });
      }
      if (phone !== this.baseService.currentUserInfo.phone) {
        this.profileService
          .updatePhone(this.profileForm.controls.phone.value)
          .pipe(switchMap(() => this.baseService.getProfile()))
          .subscribe({
            next: () => {
              this.formSubmitted = false;
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_updated,
              );
            },
            error: (res) => {
              (res.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code]);
              });
            },
          });
      }
    }
  }

  changePassword() {
    this.formSubmitted = true;
    if (this.passwordForm.valid) {
      const data = {
        new_password: this.passwordForm.controls.new_password.value,
        confirm_password: this.passwordForm.controls.confirm_password.value,
      };
      this.profileService.changePassword(data).subscribe({
        next: () => {
          this.formSubmitted = false;
          this.passwordForm.reset();
          const translation =
            activeTranslations.notifications_texts.success.password_changed ||
            activeTranslations.profile_password_success;
          this.notificationService.showSuccess('', translation);
        },
        error: (res) => {
          (res.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
    }
  }

  protected get passwordMismatch(): boolean {
    if (!this.passwordForm) {
      return false;
    }
    return (
      this.passwordForm.hasError('mismatch') &&
      (this.passwordForm.controls.confirm_password.touched || this.formSubmitted)
    );
  }

  private passwordsMatchValidator(): ValidationErrors | null {
    const newPassword = this.passwordForm?.controls?.new_password?.value;
    const confirmPassword = this.passwordForm?.controls?.confirm_password?.value;
    if (!newPassword || !confirmPassword) {
      return null;
    }
    return newPassword !== confirmPassword ? { mismatch: true } : null;
  }

  isChangeData() {
    return (
      this.profileForm.getRawValue().first_name === this.baseService.currentUserInfo.first_name &&
      this.profileForm.getRawValue().last_name === this.baseService.currentUserInfo.last_name &&
      this.profileForm.getRawValue().phone === this.baseService.currentUserInfo.phone
    );
  }

  onFileChange(event: any) {
    const files = event.target.files as FileList;
    if (files.length > 0) {
      const file = files[0];
      this.avatarForm.patchValue({ file: file });
    }
  }

  resetFile() {
    this.avatarForm.reset();
  }

  getURL() {
    return URL.createObjectURL(this.avatarForm.controls.file.value);
  }

  saveFile() {
    this.profileService
      .changeAvatar(this.avatarForm.controls.file.value)
      .pipe(switchMap(() => this.baseService.getProfile()))
      .subscribe({
        next: () => {
          this.resetFile();
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_updated,
          );
        },
        error: (res) => {
          (res.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public getPhoneValidationError(): string | null {
    if (this.profileForm && this.profileForm.controls.phone) {
      const control = this.profileForm.controls.phone;
      console.log(control);
      if (control.invalid && this.formSubmitted) {
        if (control.errors?.['required']) {
          return activeTranslations.please_enter_phone;
        } else if (control.errors?.['pattern']) {
          return activeTranslations.wrong_phone_start;
        } else if (control.errors?.['minlength']) {
          return activeTranslations.please_enter_valid_phone;
        }
      }
    }
    return null;
  }

  protected readonly ProfileTab = ProfileTab;
}
